import React from 'react';
import PropTypes from 'prop-types';
import { Cushion, Grid, Image, Space } from '@pitchero/react-ui';
import { Trans, withTranslation } from '../../lib/i18n';
import Article from '../article';
import TeamLineUp from './team-lineup';

const BREAKPOINT = 700;

const LineUp = ({
  image,
  players,
  opponents,
  starters,
  substitutes,
  overview,
  t,
  teamId,
  teamName,
}) => {
  if (!image && players.length === 0 && opponents.length === 0) {
    return (
      <Space bottom="large" component="div">
        <Article noImage title={t('teams:lineup_title')} author={null}>
          <Trans i18nKey="teams:lineup_placeholder">
            Sorry, a lineup hasn’t been published yet.
          </Trans>
        </Article>
      </Space>
    );
  }

  const clubTeam = overview.ha === 'h' ? overview.home : overview.away;
  const clubTeamLineup = players.length ? (
    <TeamLineUp
      starters={players}
      substitutes={substitutes}
      teamId={teamId}
      teamName={teamName || clubTeam.name}
      primaryColor={clubTeam.colours.primary}
      primaryContrast={clubTeam.colours.primary_contrast}
    />
  ) : null;

  const oppositionTeam = overview.ha === 'a' ? overview.home : overview.away;
  const oppositionLineup = opponents.length ? (
    <TeamLineUp
      starters={opponents.slice(0, starters)}
      substitutes={opponents.slice(starters)}
      teamName={oppositionTeam.name}
      primaryColor={oppositionTeam.colours.primary}
      primaryContrast={oppositionTeam.colours.primary_contrast}
    />
  ) : null;

  return (
    <>
      {image && (
        <Space bottom="medium">
          <Cushion horizontal="large" component="div" style={{ textAlign: 'center' }}>
            <Image alt="Selection" shimmerOnLoad src={image} maxWidth="100%" />
          </Cushion>
        </Space>
      )}
      <Grid
        columns={clubTeamLineup && oppositionLineup ? '1fr 1fr' : '1fr'}
        rowGap="medium"
        responsive={[
          {
            maxWidth: BREAKPOINT,
            props: { columns: '1fr' },
          },
        ]}
        columnGap="medium"
      >
        {/* Order of the teams is dependant on if this is a home or away fixture. */}
        {overview.ha === 'h' ? clubTeamLineup : oppositionLineup}
        {overview.ha === 'a' ? clubTeamLineup : oppositionLineup}
      </Grid>
    </>
  );
};

LineUp.defaultProps = {
  image: null,
  opponents: [],
  players: [],
  substitutes: [],
};

LineUp.propTypes = {
  image: PropTypes.string,
  starters: PropTypes.number.isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.number,
      position: PropTypes.string,
    }),
  ),
  substitutes: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.number,
      position: PropTypes.string,
    }),
  ),
  opponents: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.number,
    }),
  ),
  overview: PropTypes.shape({
    away: PropTypes.shape({
      name: PropTypes.string.isRequired,
      colours: PropTypes.shape({
        primary: PropTypes.string.isRequired,
        primary_contrast: PropTypes.string.isRequired,
      }),
    }).isRequired,
    ha: PropTypes.oneOf(['h', 'a']).isRequired,
    home: PropTypes.shape({
      name: PropTypes.string.isRequired,
      colours: PropTypes.shape({
        primary: PropTypes.string.isRequired,
        primary_contrast: PropTypes.string.isRequired,
      }),
    }).isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  teamId: PropTypes.number.isRequired,
  teamName: PropTypes.string.isRequired,
};

export default withTranslation(['teams'])(LineUp);
